import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  useDisclosure,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { AddIcon, InfoOutlineIcon, RepeatIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import ProductApi from "api/product";
import CompetitorApi from "api/competitor";
import CompetitorSearchModal from "components/Modal/CompetitorSearchModal";

const ProductAttributesCard = ({ productId, attributes, setAttributes, fetchProduct }) => {
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const { isOpen: isSearchModalOpen, onOpen: onSearchModalOpen, onClose: onSearchModalClose } = useDisclosure();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();

  const [selectedAttrId, setSelectedAttrId] = useState(null);
  const [savingAttributeId, setSavingAttributeId] = useState(null);
  const [isSyncingPrices, setIsSyncingPrices] = useState(false);

  const activeAttr = attributes.find((a) => a.id === selectedAttrId);

  const toNullableNumber = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  const normalizeNumericInput = (value) => String(value).replace(/[^\d]/g, "");
  
  const formatNumber = (value) => {
    const normalized = String(value ?? "").replace(/[^\d]/g, "");
    if (!normalized) return "";
    return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const getPlatformLabel = (platform) => {
    if (platform === 1 || platform === "1") return "PS4";
    if (platform === 2 || platform === "2") return "PS5";
    if (platform === 3 || platform === "3") return "PS4/PS5";
    if (platform === null || platform === undefined) return "";
    return String(platform).toUpperCase();
  };

  const handleAttributeField = (attributeId, key, value) => {
    setAttributes((prev) =>
      prev.map((attr) => (attr.id === attributeId ? { ...attr, [key]: value } : attr))
    );
  };

  const handleNumericAttributeField = (attributeId, key, value) => {
    handleAttributeField(attributeId, key, normalizeNumericInput(value));
  };

  const saveAttribute = async (attribute) => {
    setSavingAttributeId(attribute.id);
    try {
      const payload = {
        sku: attribute.sku,
        price: toNullableNumber(attribute.price),
      };
      await ProductApi.UpdateAttribute(productId, attribute.id, payload);
      toast({ title: `ویژگی ${attribute.id} ذخیره شد`, status: "success", duration: 2500, isClosable: true, position: "top-right" });
      onEditModalClose();
    } catch (error) {
      toast({ title: "بروزرسانی ناموفق بود", status: "error", duration: 3500, isClosable: true, position: "top-right" });
    } finally {
      setSavingAttributeId(null);
    }
  };

  const handleRemoveMapping = async (mappingId) => {
    if (!window.confirm("آیا از حذف این اتصال مطمئن هستید؟")) return;
    try {
      await CompetitorApi.RemoveMapping(mappingId);
      toast({ title: "اتصال با موفقیت حذف شد", status: "success", duration: 3000, isClosable: true, position: "top-right" });
      fetchProduct();
    } catch (error) {
      toast({ title: "خطا در حذف اتصال", description: error?.response?.data?.error || "مشکلی پیش آمد.", status: "error", duration: 3000, isClosable: true, position: "top-right" });
    }
  };

  const handleSyncPrices = async () => {
    setIsSyncingPrices(true);
    try {
      await CompetitorApi.SyncPrices({ product_id: productId });
      toast({ title: "قیمت‌های رقبا با موفقیت بروزرسانی شد", status: "success", duration: 3000, isClosable: true, position: "top-right" });
      await fetchProduct();
    } catch (error) {
      toast({ title: "خطا در همگام‌سازی", description: error?.response?.data?.message || "مشکلی پیش آمد.", status: "error", duration: 3500, isClosable: true, position: "top-right" });
    } finally {
      setIsSyncingPrices(false);
    }
  };

  const openEditModal = (attrId) => {
    setSelectedAttrId(attrId);
    onEditModalOpen();
  };

  return (
    <>
      <Card>
        <CardHeader p="6px 0px 22px 0px" display="flex" justifyContent="space-between" alignItems="center">
          <Text fontSize="xl" color={textColor} fontWeight="bold">
            ویژگی های محصول
          </Text>
          <Flex gap={3}>
            <Button size="sm" colorScheme="blue" leftIcon={<RepeatIcon />} onClick={handleSyncPrices} isLoading={isSyncingPrices} loadingText="در حال سینک...">
              سینک قیمت‌ها
            </Button>
            <Button size="sm" colorScheme="teal" leftIcon={<AddIcon />} onClick={onSearchModalOpen}>
              افزودن قیمت رقیب
            </Button>
          </Flex>
        </CardHeader>
        
        <CardBody overflowX="auto">
          <Table variant="simple" minWidth="950px">
            <Thead>
              <Tr>
                <Th>شناسه</Th>
                <Th>SKU</Th>
                <Th>موجودی</Th>
                <Th>ظرفیت / پلتفرم</Th>
                <Th textAlign="center">قیمت ما</Th>
                <Th>قیمت رقبا</Th>
                <Th textAlign="center">عملیات</Th>
              </Tr>
            </Thead>
            <Tbody>
              {attributes.map((attribute) => (
                <Tr key={attribute.id}>
                  <Td fontWeight="bold">{attribute.id}</Td>
                  <Td>{attribute.sku}</Td>
                  <Td>{attribute.stock}</Td>
                  <Td>
                    <Badge colorScheme="blue" mx={1}>Z{attribute.z_type}</Badge>
                    <Badge colorScheme="gray">{getPlatformLabel(attribute.platform)}</Badge>
                  </Td>
                  <Td dir="ltr" textAlign="center" fontWeight="bold">
                    <Badge colorScheme="blue" variant="solid" px={2} py={1} fontSize="sm">
                      {formatNumber(attribute.price)}
                    </Badge>
                  </Td>
                  
                  <Td>
                    {attribute.competitor_attributes?.length > 0 ? (
                      <Flex direction="column" gap={2} minW="240px">
                        {attribute.competitor_attributes.map((competitor) => {
                          const myPrice = Number(attribute.price) || 0;
                          const compPrice = Number(competitor.price) || 0;
                          const diff = compPrice - myPrice;
                          const percent = myPrice > 0 ? ((diff / myPrice) * 100).toFixed(1) : 0;
                          const isLower = compPrice > 0 && compPrice < myPrice;
                          const isHigher = compPrice > myPrice;
                          const priceColor = isLower ? "red.500" : isHigher ? "green.500" : "inherit";

                          return (
                            <Flex 
                              key={competitor.id} 
                              borderWidth="1px"
                              borderColor={borderColor}
                              borderRadius="md" 
                              px={3} py={1.5} 
                              display="flex" 
                              justifyContent="space-between" 
                              alignItems="center"
                            >
                              <Flex alignItems="center" gap={1.5}>
                                <Text as="span" fontSize="sm" fontWeight="500">
                                  {competitor.competitor_name || "-"}
                                </Text>
                                
                                <Tooltip hasArrow placement="top" label={
                                  <Box textAlign="right" dir="rtl" px={2} py={1} maxW="250px">
                                    <Text fontSize="xs" fontWeight="normal" mb={1.5}>
                                      {competitor.raw_label || "نام خام یافت نشد"}
                                    </Text>
                                    {competitor.checked_at && (
                                      <Flex justify="space-between" align="center" borderTop="1px solid" borderColor="gray.500" pt={1}>
                                        <Text fontSize="10px" color="gray.300">
                                          بررسی: {new Date(competitor.checked_at).toLocaleDateString('fa-IR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </Text>
                                        <Text fontSize="10px" fontWeight="bold" color={competitor.in_stock ? "green.300" : "red.300"}>
                                          {competitor.in_stock ? "موجود" : "ناموجود"}
                                        </Text>
                                      </Flex>
                                    )}
                                  </Box>
                                }>
                                  <InfoOutlineIcon boxSize={2.5} color="gray.500" cursor="help" />
                                </Tooltip>
                              </Flex>

                              <Flex dir="ltr" gap={2} alignItems="center">
                                <CloseIcon 
                                  boxSize={2.5} 
                                  color="gray.400" 
                                  cursor="pointer" 
                                  mr={1} 
                                  _hover={{ color: "red.500" }} 
                                  onClick={() => handleRemoveMapping(competitor.id)} 
                                />

                                <Text as="span" fontSize="md" fontWeight="900" color={priceColor}>
                                  {formatNumber(competitor.price)}
                                </Text>
                                {myPrice > 0 && compPrice > 0 && diff !== 0 && (
                                  <Text as="span" fontSize="xs" fontWeight="bold" color={priceColor}>
                                    ({percent > 0 ? `+${percent}` : percent}%)
                                  </Text>
                                )}
                              </Flex>
                            </Flex>
                          );
                        })}
                      </Flex>
                    ) : (
                      <Badge colorScheme="gray" variant="outline">بدون رقیب</Badge>
                    )}
                  </Td>

                  <Td textAlign="center">
                    <Button size="sm" colorScheme="orange" leftIcon={<EditIcon />} onClick={() => openEditModal(attribute.id)}>
                      ویرایش
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      <CompetitorSearchModal 
        isOpen={isSearchModalOpen} 
        onClose={onSearchModalClose} 
        productId={productId} 
        onMappingSuccess={fetchProduct} 
      />

      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent dir="rtl">
          <ModalHeader>ویرایش ویژگی #{activeAttr?.id}</ModalHeader>
          <ModalCloseButton right="unset" left="4" top="4" />
          <ModalBody pb={6}>
            {activeAttr && (
              <Flex direction="column" gap={6}>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl>
                    <FormLabel>SKU</FormLabel>
                    <Input textAlign="left" value={activeAttr.sku} onChange={(e) => handleAttributeField(activeAttr.id, "sku", e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>قیمت</FormLabel>
                    <Input type="text" inputMode="numeric" textAlign="left" dir="ltr" fontWeight="bold" value={formatNumber(activeAttr.price)} onChange={(e) => handleNumericAttributeField(activeAttr.id, "price", e.target.value)} />
                  </FormControl>
                </Grid>

                <Box>
                  <Text fontWeight="bold" mb={3}>قیمت‌های رقبا متصل به این ویژگی:</Text>
                  {activeAttr.competitor_attributes?.length > 0 ? (
                    <Flex direction="column" gap={3}>
                      {activeAttr.competitor_attributes.map((competitor) => {
                        const myPrice = Number(activeAttr.price) || 0;
                        const compPrice = Number(competitor.price) || 0;
                        const diff = compPrice - myPrice;
                        const percent = myPrice > 0 ? ((diff / myPrice) * 100).toFixed(1) : 0;
                        const isLower = compPrice > 0 && compPrice < myPrice;
                        const isHigher = compPrice > myPrice;
                        const priceColor = isLower ? "red.500" : isHigher ? "green.500" : "inherit";

                        return (
                          <Flex 
                            key={competitor.id} 
                            borderWidth="1px"
                            borderColor={borderColor}
                            borderRadius="md" 
                            px={3} py={2} 
                            width="100%" 
                            display="flex" 
                            alignItems="center" 
                            justifyContent="space-between"
                          >
                            <Flex alignItems="center" gap={2}>
                              <Text as="span" fontSize="sm" fontWeight="500">{competitor.competitor_name || "-"}</Text>
                              <Tooltip hasArrow placement="top" label={
                                <Box textAlign="right" dir="rtl" px={2} py={1} maxW="250px">
                                  <Text fontSize="xs" fontWeight="normal" mb={1.5}>
                                    {competitor.raw_label || "نام خام یافت نشد"}
                                  </Text>
                                  {competitor.checked_at && (
                                    <Flex justify="space-between" align="center" borderTop="1px solid" borderColor="gray.500" pt={1}>
                                      <Text fontSize="10px" color="gray.300">
                                        بررسی: {new Date(competitor.checked_at).toLocaleDateString('fa-IR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                      </Text>
                                      <Text fontSize="10px" fontWeight="bold" color={competitor.in_stock ? "green.300" : "red.300"}>
                                        {competitor.in_stock ? "موجود" : "ناموجود"}
                                      </Text>
                                    </Flex>
                                  )}
                                </Box>
                              }>
                                <InfoOutlineIcon boxSize={3} color="gray.500" cursor="help" />
                              </Tooltip>
                            </Flex>

                            <Flex alignItems="center" gap={2} dir="ltr">
                              <CloseIcon boxSize={3} color="gray.400" cursor="pointer" mr={3} _hover={{ color: "red.500" }} onClick={() => handleRemoveMapping(competitor.id)} />
                              <Text as="span" fontSize="md" fontWeight="900" color={priceColor}>{formatNumber(competitor.price)}</Text>
                              {myPrice > 0 && compPrice > 0 && diff !== 0 && (
                                <Text as="span" fontSize="xs" fontWeight="bold" color={priceColor}>({percent > 0 ? `+${percent}` : percent}%)</Text>
                              )}
                            </Flex>
                          </Flex>
                        );
                      })}
                    </Flex>
                  ) : (
                    <Text fontSize="sm" color="gray.500">هیچ رقیبی برای این ظرفیت متصل نشده است.</Text>
                  )}
                </Box>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderTopWidth="1px">
            <Button variant="ghost" mr={3} onClick={onEditModalClose}>بستن</Button>
            <Button colorScheme="blue" onClick={() => saveAttribute(activeAttr)} isLoading={savingAttributeId === activeAttr?.id}>
              ذخیره تغییرات
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProductAttributesCard;